// TODO(dev:dashboard_v2, translations): Add missing translations at formatTimeAgo
import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"
import {
  formatDistanceToNowStrict,
  differenceInWeeks,
  differenceInMonths,
} from "date-fns"
import { Icon } from "../ui/icon"

// TODO(dev:dashboard_v2): Extend eventType, currently limited to funding, promotion, invalid_email
type EventType =
  | "email_added"
  | "technology_added"
  | "funding_added"
  | "linkedin_job_added"
  | "technology_deleted"
  | "executive_added"
  | "spike_metric"
  | "position_changed"
  | "location_changed"
type EntityType = "domain" | "email"

const leadSignalItemVariants = cva("lead-signal-item", {
  variants: {
    visited: {
      false: "lead-signal-item--unread",
    },
  },
  defaultVariants: {
    visited: false,
  },
})

interface LeadSignalItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof leadSignalItemVariants> {
  message: string
  timestamp: Date
  eventType: EventType
  entityType: EntityType
  count: number
  onClick?: () => void
}

interface LeadSignalAvatarProps {
  type: EventType
}

const LeadSignalAvatar = ({ type }: LeadSignalAvatarProps) => {
  return (
    <div
      className={cn("lead-signal-avatar", {
        "lead-signal-avatar--error": type === "location_changed",
      })}
    >
      {type === "email_added" && <Icon icon="envelope" size="sm" />}
      {type === "technology_added" && <Icon icon="microchip" size="sm" />}
      {type === "funding_added" && <Icon icon="money-bill" size="sm" />}
      {type === "linkedin_job_added" && <Icon icon="linkedin" size="sm" />}
      {type === "technology_deleted" && <Icon icon="microchip" size="sm" />}
      {type === "executive_added" && <Icon icon="user" size="sm" />}
      {type === "spike_metric" && <Icon icon="chart-line" size="sm" />}
      {type === "position_changed" && <Icon icon="handshake" size="sm" />}
      {type === "location_changed" && <Icon icon="shield-xmark" size="sm" />}
    </div>
  )
}

function formatTimeAgo(timestamp: Date): string {
  const now = new Date()
  const weeks = differenceInWeeks(now, timestamp)
  const months = differenceInMonths(now, timestamp)

  if (weeks >= 1 && weeks < 4) {
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`
  } else if (weeks >= 4 || months >= 1) {
    const monthsToShow = months >= 1 ? months : 1
    return `${monthsToShow} ${monthsToShow === 1 ? "month" : "months"} ago`
  } else {
    return formatDistanceToNowStrict(timestamp, {
      addSuffix: true,
    })
  }
}

const LeadSignalItem = React.forwardRef<HTMLDivElement, LeadSignalItemProps>(
  (
    {
      className,
      message,
      timestamp,
      eventType,
      entityType,
      count,
      onClick,
      visited,
      ...props
    },
    ref,
  ) => {
    const formattedTime = formatTimeAgo(timestamp)

    return (
      <article
        ref={ref}
        className={cn(leadSignalItemVariants({ visited }), className)}
        {...props}
      >
        <button
          className="lead-signal-item__link"
          onClick={onClick}
          aria-label={`View signal: ${message}`}
        >
          <span className="sr-only">{`View signal: ${message}`}</span>
        </button>
        <LeadSignalAvatar type={eventType} />
        <div className="lead-signal-item__main">
          <span className="lead-signal-item__content">{message}</span>
          <span className="lead-signal-item__timestamp">{formattedTime}</span>
        </div>
        {!visited && <span className="lead-signal-item__dot" />}
      </article>
    )
  },
)

LeadSignalItem.displayName = "LeadSignalItem"

export { LeadSignalItem, type EventType }
